import type { ReactNode } from 'react';
import { formatTime } from '../hooks/useGameBoard';
import { useI18n } from '../i18n';

interface GameStatusBarProps {
  elapsed: number;
  mistakes: number;
  maxMistakes: number;
  /** Skor: görsel "{n}p", a11y/title "Puan: {n}". Verilmezse çizilmez. */
  score?: number;
  /** Skor animasyonu (doğru hamlede pulse) */
  scorePulsing?: boolean;
  /** Zorluk metni (t.difficulties[diff]); verilmezse çizilmez */
  difficultyLabel?: string;
  /** Opsiyonel zorluk rengi (DIFF_COLORS[diff]) */
  difficultyColor?: string;
  isPaused?: boolean;
  onTogglePause?: () => void;
  /** RoomPage gibi ekranlar için ek class (ör. "mp-status-bar") */
  className?: string;
  // — Moda özgü slot'lar —
  leadingActions?: ReactNode;
  centerExtra?: ReactNode;
  targetTimeSlot?: ReactNode;
  roomShareSlot?: ReactNode;
  opponentRatingSlot?: ReactNode;
  challengeSlot?: ReactNode;
}

/**
 * Web'de tüm oynanabilir board'ların ÜSTÜNDEKİ ortak bilgi satırı.
 * Çekirdek (pause · timer · score · mistakes · difficulty) her modda aynı biçimde;
 * moda özgü kısımlar adlandırılmış slot'larla eklenir. Sadece görünüm — iş mantığı
 * çağıran sayfada kalır. Stiller ortak GamePage.css'te (tüm oyun sayfaları import eder).
 */
export function GameStatusBar({
  elapsed,
  mistakes,
  maxMistakes,
  score,
  scorePulsing,
  difficultyLabel,
  difficultyColor,
  isPaused,
  onTogglePause,
  className,
  leadingActions,
  centerExtra,
  targetTimeSlot,
  roomShareSlot,
  opponentRatingSlot,
  challengeSlot,
}: GameStatusBarProps) {
  const { t } = useI18n();
  const scoreA11y = score != null ? `${t.room.scoreLabel}: ${score}` : undefined;

  return (
    <div className={`board-status-bar${className ? ` ${className}` : ''}`}>
      <div className="status-group status-left">
        {onTogglePause && (
          <button className="pause-btn" onClick={onTogglePause} title={isPaused ? t.game.resumeBtn : t.game.pauseBtn}>
            {isPaused ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6,4 20,12 6,20" /></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="5" y="4" width="4" height="16" rx="1" /><rect x="15" y="4" width="4" height="16" rx="1" /></svg>
            )}
          </button>
        )}
        {leadingActions}
        <span className="timer">{formatTime(elapsed)}</span>
      </div>

      <div className="status-group status-center">
        {score != null && (
          <span className={`score-chip${scorePulsing ? ' score-pulse' : ''}`} title={scoreA11y} aria-label={scoreA11y}>
            {score}p
          </span>
        )}
        {centerExtra}
        {score != null && <span className="status-sep">·</span>}
        <span className="mistakes-chip">{mistakes}/{maxMistakes}</span>
      </div>

      <div className="status-group status-right">
        {targetTimeSlot}
        {opponentRatingSlot}
        {challengeSlot}
        {roomShareSlot}
        {difficultyLabel != null && (
          <span className="diff-badge" style={difficultyColor ? { color: difficultyColor } : undefined}>
            {difficultyLabel}
          </span>
        )}
      </div>
    </div>
  );
}
